import { t } from 'i18next';
import { set } from 'lodash';
import moment from 'moment';
import {
  AccountInfoType,
  ConditionsType,
  ContactFormType,
  FieldChoicesType,
  ScalePropertiesRangeType,
} from 'shared/types';
import {
  CHECK_TRUTH,
  DATETIME,
  FILTER_CONDITIONS,
  FILTER_NUMBER_CONDITIONS,
  FieldType,
} from './constants';
import { FieldsType } from '.';

type RangeType = { start: string; end: string };

export const getRangeValue = (
  valueType: string,
  accountInfo?: AccountInfoType,
): RangeType | undefined => {
  switch (valueType) {
    case 'today':
      return {
        start: moment()
          .tz(accountInfo?.timezone || moment.tz.guess(), true)
          .startOf('day')
          .toISOString(),
        end: moment()
          .tz(accountInfo?.timezone || moment.tz.guess(), true)
          .endOf('day')
          .toISOString(),
      };
    case 'yesterday':
      return {
        start: moment()
          .tz(accountInfo?.timezone || moment.tz.guess(), true)
          .subtract(1, 'day')
          .startOf('day')
          .toISOString(),
        end: moment()
          .tz(accountInfo?.timezone || moment.tz.guess(), true)
          .startOf('day')
          .toISOString(),
      };
    case 'last7days':
      return {
        start: moment()
          .tz(accountInfo?.timezone || moment.tz.guess(), true)
          .subtract(7, 'days')
          .toISOString(),
        end: moment()
          .tz(accountInfo?.timezone || moment.tz.guess(), true)
          .toISOString(),
      };
    case 'last30days':
      return {
        start: moment()
          .tz(accountInfo?.timezone || moment.tz.guess(), true)
          .subtract(30, 'days')
          .toISOString(),
        end: moment()
          .tz(accountInfo?.timezone || moment.tz.guess(), true)
          .toISOString(),
      };
  }
};

export const getChoices = (
  name: string,
  contactForm: ContactFormType | undefined,
) => {
  const selected = contactForm?.fields?.find((item: any) => item.name === name);
  const choices = selected?.choices?.map((c: FieldChoicesType) => {
    if (c.id && c.value)
      return {
        id: c?.id,
        value: c?.id,
        name: c?.value,
      };
  });
  return choices || [];
};

export const constructFilterFields = ({
  field,
  prefix,
  contactForm,
}: {
  field: FieldsType;
  prefix?: string;
  contactForm?: ContactFormType | undefined;
}) => {
  const { name, type, label } = field;
  const fieldId: string = prefix ? `${prefix}.${name}` : name;
  switch (type) {
    case 'DROPDOWN':
      return {
        id: fieldId,
        uniqueId: 'contact_field',
        key: fieldId,
        name: label,
        label: name,
        type,
        placeholder: 'forms.placeholders.any',
        defaultValue: '',
        options: contactForm ? getChoices(field.name, contactForm) || [] : [],
      };
    case 'CHECKBOX':
      return {
        id: fieldId,
        uniqueId: 'contact_field',
        key: fieldId,
        name: label,
        label: name,
        type,
        placeholder: 'forms.placeholders.any',
        defaultValue: '',
        options: CHECK_TRUTH,
      };
    case 'MULTI_SELECT':
      return {
        id: fieldId,
        uniqueId: 'contact_field',
        key: fieldId,
        name: label,
        label: name,
        type,
        placeholder: 'forms.placeholders.any',
        defaultValue: '',
        options: contactForm ? getChoices(field.name, contactForm) || [] : [],
      };
    case 'NUMBER':
      return {
        id: fieldId,
        uniqueId: 'contact_field',
        key: fieldId,
        name: label,
        label: name,
        type: 'DROPDOWN',
        placeholder: 'forms.placeholders.any',
        defaultValue: '',
        options: FILTER_CONDITIONS.map((item: ConditionsType) => ({
          ...item,
          conditionType: 'NUMBER',
        })),
        subComponent: {
          placeholder: 'forms.placeholders.enter_number',
        },
      };
    case 'EMAIL':
    case 'TEXT':
      return {
        id: fieldId,
        uniqueId: 'contact_field',
        key: fieldId,
        name: label,
        label: name,
        type,
        placeholder: 'forms.placeholders.any',
        defaultValue: '',
        options: [],
      };
    case 'DATE_TIME':
      return {
        id: fieldId,
        uniqueId: 'contact_field',
        key: fieldId,
        name: label,
        label: name,
        type: 'DROPDOWN',
        placeholder: 'forms.placeholders.any',
        defaultValue: '',
        options: DATETIME,
        subComponent: {
          placeholder: 'forms.placeholders.select_date',
        },
      };
  }
};

export const constructFilterURL = (
  filterObj: any,
  FilterFields: Array<FieldType>,
  accountInfo?: AccountInfoType,
) => {
  let urlConstruct = '';
  Object.keys(filterObj).forEach((key: string) => {
    const limiter = urlConstruct.length ? '&' : '';
    if (filterObj[key] !== '') {
      if (typeof filterObj[key] === 'string') {
        urlConstruct += `${limiter}${key}=${encodeURIComponent(
          filterObj[key].trim(),
        )}`;
      } else if (
        filterObj[key]?.condition &&
        typeof filterObj[key]?.value === 'string'
      ) {
        urlConstruct += `${limiter}${key}${
          filterObj[key].condition.value
        }${encodeURIComponent(filterObj[key].value)}`;
      } else {
        const filterKey =
          FilterFields.find((item: FieldType) => item.id === key)?.key || key;
        switch (filterObj[key]?.type) {
          case 'DATERANGE': {
            const range = getRangeValue(filterObj[key]?.id, accountInfo);
            if (range)
              urlConstruct += `${limiter}${filterKey}${encodeURIComponent(
                '[gte]',
              )}=${range?.start}&${filterKey}${encodeURIComponent('[lte]')}=${
                range?.end
              }`;
            break;
          }
          case 'MULTISELECT': {
            const { value }: any = filterObj[key];
            value.forEach((item: any, index: number) => {
              urlConstruct += index
                ? `&${key}=${item.id ?? item.display_id}`
                : `${limiter}${key}=${encodeURIComponent(
                    item.id ?? item.display_id,
                  )}`;
            });
            break;
          }
          case 'range':
            urlConstruct += `${limiter}${filterKey}${encodeURIComponent(
              '[gte]',
            )}=${filterObj[key]?.value?.start}&${filterKey}${encodeURIComponent(
              '[lte]',
            )}=${filterObj[key]?.value?.end}`;
            break;
          default:
            break;
        }
      }
    }
  });
  return urlConstruct;
};

export const conditionalFieldsForFilters = [
  'ratings.csat_rating',
  'ratings.csat_rating[gt]',
  'ratings.csat_rating[lt]',
  'ratings.csat_rating[lte]',
  'ratings.csat_rating[gte]',
  'ratings.prev_csat_rating',
  'ratings.prev_csat_rating[gt]',
  'ratings.prev_csat_rating[lt]',
  'ratings.prev_csat_rating[lte]',
  'ratings.prev_csat_rating[gte]',
  'ratings.nps_rating',
  'ratings.nps_rating[gt]',
  'ratings.nps_rating[lt]',
  'ratings.nps_rating[lte]',
  'ratings.nps_rating[gte]',
  'ratings.prev_nps_rating',
  'ratings.prev_nps_rating[gt]',
  'ratings.prev_nps_rating[lt]',
  'ratings.prev_nps_rating[lte]',
  'ratings.prev_nps_rating[gte]',
  'ratings.ces_rating',
  'ratings.ces_rating[gt]',
  'ratings.ces_rating[lt]',
  'ratings.ces_rating[lte]',
  'ratings.ces_rating[gte]',
  'ratings.prev_ces_rating',
  'ratings.prev_ces_rating[gt]',
  'ratings.prev_ces_rating[lt]',
  'ratings.prev_ces_rating[lte]',
  'ratings.prev_ces_rating[gte]',
];

export const NumberConditionalFieldsForFilter = [
  'ratings.total_given_score',
  'ratings.score_percent',
];

export const DATE_FIELDS = [
  'created_at',
  'created_at[lt]',
  'created_at[gt]',
  'created_at[gte]',
  'created_at[lte]',
  'contact_response.date',
];

const multiSelectFields = ['contact_response.list_ids'];

export const removeDateFields = (obj) => {
  DATE_FIELDS.forEach((field) => {
    delete obj[field];
  });
};
export const constructLocalStateFilters = (
  params: string,
  dateTimeObj: Record<string, any> = {},
  contactLists: any,
): any => {
  const urlParamsObject = urlParamsToObject(params);
  const outputObject = {};

  Object.keys(urlParamsObject).forEach((key: string) => {
    // checking if it is not an "is" condition, then getting the value from regex
    if (
      conditionalFieldsForFilters.includes(key) ||
      NumberConditionalFieldsForFilter.includes(key)
    ) {
      const selectedFilterConditions = conditionalFieldsForFilters.includes(key)
        ? FILTER_CONDITIONS
        : FILTER_NUMBER_CONDITIONS;
      const regexMatch = key.match('\\[(.*?)\\]');
      const conditionId = regexMatch
        ? regexMatch[1]
        : urlParamsObject[key] === '\u0000'
        ? 'is_empty'
        : 'is';
      const conditionObject = selectedFilterConditions.find(
        (item) => item.id == conditionId,
      );
      // to get only the field without [gte] or [lte]
      const requiredKey = key.replace(/\[[^\]]+\]/g, '');
      outputObject[requiredKey] = {
        key: requiredKey,
        value: urlParamsObject[key],
        condition: conditionObject,
      };
      // } else if (DATE_FIELDS.includes(key)) {
      //   // whatever be the value, we'll convert it to timestamp
      //   const condition = DATETIME.find((item) => item.id);
      //   console.log('condition', condition);

      //   if (condition) {
      //     if (key.includes('[gte]'))
      //       condition.value['start'] = urlParamsObject[key];
      //     if (key.includes('[lte]'))
      //       condition.value['end'] = urlParamsObject[key];
      //   }
      //   outputObject[key.replace(/\[[^\]]+\]/g, '')] = condition;
    } else if (multiSelectFields.includes(key)) {
      const lists = urlParamsObject[key].split(',');
      outputObject[key] = {
        type: 'MULTISELECT',
        value: contactLists?.filter((list) => lists.includes(list.display_id)),
      };
    } else {
      outputObject[key] = urlParamsObject[key];
    }
  });
  if (dateTimeObj.length) {
    outputObject['created_at'] = dateTimeObj[0];
  }
  return outputObject;
};

export const urlParamsToObject = (urlParams) => {
  const params = new URLSearchParams(urlParams);
  const obj = {};

  // checking if the key already exists then appending it else creating the key
  for (const [key, value] of params.entries()) {
    if (obj[key]) {
      obj[key] = obj[key].concat(',', value);
    } else {
      obj[key] = value;
    }
  }
  return obj;
};

export const constructFilterPayload = (
  filterObj: any,
  FilterFields: Array<FieldType>,
  accountInfo?: AccountInfoType,
) => {
  const filterPayload = {};

  Object.keys(filterObj).forEach((key: string) => {
    if (filterObj[key] !== '') {
      if (typeof filterObj[key] === 'string') {
        set(filterPayload, key, filterObj[key].trim());
      } else if (
        filterObj[key]?.condition &&
        typeof filterObj[key]?.value === 'string'
      ) {
        const condition: 'gt' | 'gte' | 'lt' | 'lte' | 'is' =
          filterObj[key].condition.id;
        set(
          filterPayload,
          `${key}${condition !== 'is' ? `_${condition}` : ''}`,
          filterObj[key].value,
        );
      } else {
        const filterKey =
          FilterFields.find((item: FieldType) => item.id === key)?.key || key;
        switch (filterObj[key]?.type) {
          case 'DATERANGE': {
            const range = getRangeValue(filterObj[key]?.id, accountInfo);
            if (range) {
              set(filterPayload, `${filterKey}_gte`, range?.start);
              set(filterPayload, `${filterKey}_lte`, range?.end);
            }
            break;
          }
          case 'MULTISELECT': {
            const { value }: any = filterObj[key];
            set(
              filterPayload,
              key,
              value.map((item: any) => item.display_id),
            );
            break;
          }
          case 'range':
            set(
              filterPayload,
              `${filterKey}_gte`,
              filterObj[key]?.value?.start,
            );
            set(filterPayload, `${filterKey}_lte`, filterObj[key]?.value?.end);
            break;
          default:
            break;
        }
      }
    }
  });

  return filterPayload;
};

export const getRatingOptions = (range: ScalePropertiesRangeType[]) => {
  if (range.length) {
    const min = Number(range[0].min);
    const max = Number(range[range.length - 1].max);
    const ratingOptions: any = [];
    for (let i = min; i <= max; i++) {
      const choice = {
        id: String(i),
        name: String(i),
        value: String(i),
      };
      ratingOptions.push(choice);
    }
    return ratingOptions;
  }
  return [];
};

type RatingGroupType =
  | 'promoter'
  | 'detractor'
  | 'passive'
  | 'positive'
  | 'neutral'
  | 'negative';

const RATING_GROUP_MATCH: Record<RatingGroupType, string> = {
  promoter: 'rating_group.promoter',
  passive: 'rating_group.passive',
  detractor: 'rating_group.detractor',
  positive: 'rating_group.positive',
  neutral: 'rating_group.neutral',
  negative: 'rating_group.negative',
};

export const defaultScoreGroup = () => {
  return [
    {
      id: 'positive',
      name: `${t(RATING_GROUP_MATCH['positive'])}`,
      value: 'positive',
    },
    {
      id: 'neutral',
      name: `${t(RATING_GROUP_MATCH['neutral'])}`,
      value: 'neutral',
    },
    {
      id: 'negative',
      name: `${t(RATING_GROUP_MATCH['negative'])}`,
      value: 'negative',
    },
  ];
};

export const getRatingOptionsGroup = (options: any) => {
  if (options?.length) {
    const ratingGroups = options.map(
      (option: { label: string; min: number; max: number }) => {
        const group = {
          id: option.label,
          name: `${t(RATING_GROUP_MATCH[option.label])}`,
          value: option.label,
        };
        return group;
      },
    );
    return ratingGroups.sort().reverse();
  }
  return [];
};
